import ActiveStatusItem from "./ActiveStatusItem";
import CompletedStatusItem, { type CompletedResearchStep } from "./CompletedStatusItem";
import styles from "./DeepResearchChatStatus.module.css";
import { deepResearchColor } from "@app/config/constants";
import type { ResearchSession } from "@globals";
import Icon from "@lib/common/components/Icon/Icon";
import { ActionIcon, Box, Button, Group, ScrollArea, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { useClickOutside, useToggle } from "@mantine/hooks";
import type { MouseEvent } from "react";
import { Link } from "react-router";

/** Collapsed bar height; the expanded overlay grows upward from its bottom edge. */
const BAR_HEIGHT = 38;
const MAX_COMPLETED_STEPS = 10;

export type DeepResearchChatStatusProps = {
  session: Pick<ResearchSession, "status" | "progress" | "progress_pct" | "created_at">;
  /** Newest first. Empty today: a durable step history is deferred (ADR 0014), and the session keeps one progress line. */
  completedSteps?: CompletedResearchStep[];
  /** Where "Go to details" leads: the report once the run is done, the research tab otherwise. */
  detailsTo: string;
  /** The user has followed the done bar to its report, so the owner can drop it. */
  onDetailsClick?: () => void;
  onCancel: () => void;
  isCancelling?: boolean;
};

/**
 * Deep-research run status above the chat compose area: the latest progress update with a cancel
 * action while in flight, a link to the result once done. It expands into an overlay of completed
 * updates, and is not clickable without any — there would be nothing to reveal.
 */
export default function DeepResearchChatStatus({
  session,
  completedSteps = [],
  detailsTo,
  onDetailsClick,
  onCancel,
  isCancelling = false
}: DeepResearchChatStatusProps) {
  const [expanded, toggle] = useToggle();
  const ref = useClickOutside(() => toggle(false));

  if (session.status === "SUCCEEDED") {
    return (
      // Link inside a div: as the root element it picks up app link styling and loses the card chrome.
      <UnstyledButton component="div" classNames={{ root: styles.bar }} data-complete h={BAR_HEIGHT} w="100%">
        <Link to={detailsTo} onClick={onDetailsClick}>
          <Group gap={9} px="xs" h="100%" wrap="nowrap">
            <Icon name="circle-check" color={deepResearchColor} size="1rem" variant="solid" />
            <Text size="sm" fw={500} lineClamp={1} c="var(--mantine-color-text)">
              Research complete
            </Text>
            <Text size="sm" fw={500} lineClamp={1} c="dimmed">
              Click to open
            </Text>
          </Group>
        </Link>
      </UnstyledButton>
    );
  }

  const canExpand = completedSteps.length > 0;
  const label = `Research: ${session.progress ?? "starting…"}`;
  const startedAt = new Date(session.created_at);

  // The bar toggles expansion, so its children keep their clicks from bubbling into it.
  const cancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  };

  const followLink = (event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation();

  return (
    <Box ref={ref} pos="relative" h={BAR_HEIGHT}>
      <Box pos="absolute" bottom={0} left={0} right={0}>
        <UnstyledButton
          component="div"
          onClick={canExpand ? () => toggle() : undefined}
          classNames={{ root: styles.bar }}
          data-expanded={expanded || undefined}
          data-expandable={canExpand || undefined}
          w="100%"
        >
          {expanded ? (
            <ScrollArea.Autosize mah="30vh" type="always" scrollbars="y">
              <Stack gap="sm" p="md">
                <ActiveStatusItem label={label} startedAt={startedAt} />
                {completedSteps.slice(0, MAX_COMPLETED_STEPS).map((step, index) => (
                  <CompletedStatusItem key={index} step={step} />
                ))}
                <Button component={Link} to={detailsTo} variant="light" size="xs" onClick={followLink}>
                  Go to research tab
                </Button>
              </Stack>
            </ScrollArea.Autosize>
          ) : (
            <Group wrap="nowrap" justify="space-between" h={BAR_HEIGHT} pl="sm" pr={5}>
              <ActiveStatusItem label={label} startedAt={startedAt} />
              <Tooltip label="Cancel deep research">
                <ActionIcon variant="subtle" onClick={cancel} size="md" radius="md" loading={isCancelling}>
                  <Icon name="stop" variant="solid" color="dimmed" />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </UnstyledButton>
      </Box>
    </Box>
  );
}
